import { sanitizeSegment } from "../../storage/fsa.ts";
import { shortHash } from "../../storage/hash.ts";
import type { MailFolder } from "./types.ts";

/**
 * Output tree for Mail (ARCHITECTURE.md §7):
 *
 *   mail/<mailbox>/<folder path>/<date>_<time>_<hash>.eml
 *   mail/<mailbox>/<folder path>/_index.json
 *
 * Unlike Teams' chat-key (a label plus a hash suffix, since two chats can
 * share a topic), mail folder paths are already unique — Graph does not allow
 * two sibling folders with the same name — so no disambiguating suffix is
 * needed here.
 */

/**
 * Resolves every folder's full path by walking its parentFolderId chain.
 *
 * A folder whose parent is not in `folders` is treated as top-level — that is
 * the normal case (the true parent, msgfolderroot, is never returned by
 * Graph) — not an error.
 *
 * Walked iteratively per folder, each with its own `seen` set, rather than
 * with memoized recursion: an earlier recursive version's cycle guard stopped
 * infinite recursion but let a partial path already computed for one folder
 * in the cycle bubble back up and get appended to again by another — no
 * crash, but a silently wrong, growing path. Malformed/cyclic parent data
 * should not occur in a real mailbox, but the fallback needs to actually be
 * correct, not just non-crashing.
 */
export function resolveFolderPaths(folders: readonly MailFolder[]): Map<string, string[]> {
    const byId = new Map(folders.map((f) => [f.id, f]));
    const result = new Map<string, string[]>();

    for (const folder of folders) {
        const chain: string[] = [];
        const seen = new Set<string>();
        let current: MailFolder | undefined = folder;

        while (current && !seen.has(current.id)) {
            seen.add(current.id);
            chain.push(current.displayName);
            current = current.parentFolderId ? byId.get(current.parentFolderId) : undefined;
        }

        chain.reverse();
        result.set(folder.id, chain);
    }

    return result;
}

/** Joins a resolved path into a sanitized, forward-slash relative directory. */
export function folderRelativeDir(mailboxLabel: string, folderPath: readonly string[]): string {
    return [sanitizeSegment(mailboxLabel), ...folderPath.map((s) => sanitizeSegment(s))].join("/");
}

/**
 * `2024-03-14_143022` from an ISO timestamp, or a fixed fallback when the
 * message has none — still a valid, deterministic filename component either
 * way, since the hash suffix is what actually guarantees uniqueness.
 */
function dateStamp(receivedDateTime: string | undefined): string {
    if (!receivedDateTime) {
        return "unknown-date";
    }
    const date = new Date(receivedDateTime);
    if (Number.isNaN(date.getTime())) {
        return "unknown-date";
    }

    const pad = (n: number) => String(n).padStart(2, "0");
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    );
}

/** Filename for one message: `<date>_<time>_<hash>.eml`, per §7. */
export async function messageFilename(
    messageId: string,
    receivedDateTime: string | undefined
): Promise<string> {
    const hash = await shortHash(messageId, 10);
    return `${dateStamp(receivedDateTime)}_${hash}.eml`;
}

export function messagePath(
    mailboxLabel: string,
    folderPath: readonly string[],
    filename: string
): string {
    return `mail/${folderRelativeDir(mailboxLabel, folderPath)}/${filename}`;
}

export function folderIndexPath(mailboxLabel: string, folderPath: readonly string[]): string {
    return `mail/${folderRelativeDir(mailboxLabel, folderPath)}/_index.json`;
}
